import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Optional,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { PageComponent, PageHeaderComponent } from '@softapps/core';
import {
  Application,
  ApplicationStore,
  LinkModule,
  Refreshable,
  RefreshService,
  SettingsStore,
  SOFTLINE_FEATURE_APPLICATIONS,
  SOFTLINE_FEATURE_SETTINGS,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { AuthorizationStore, SOFTLINE_FEATURE_AUTHORIZATION } from '@softline/auth';
import { Store } from '@softline/core';
import {
  SOFTLINE_FEATURE_TRANSLATION,
  TranslationStore,
  UiCoreModule,
} from '@softline/ui-core';
import { Observable } from 'rxjs';
import {
  SOFTLINE_CONFIG_MODULES,
  SOFTLINE_CONFIG_MODULES_STATIC_LINKS,
} from '../module.providers';
import { ModuleSettings } from '../module.settings';
import { SOFTLINE_SETTINGS_APP_OVERVIEW } from '../module.shared';
import { AppGridComponent } from './app-grid/app-grid.component';
import { AppListComponent } from './app-list/app-list.component';

@Component({
  selector: 'soft-app-overview',
  templateUrl: './app-overview.component.html',
  styleUrls: ['./app-overview.component.scss'],
  imports: [
    CommonModule,
    UiCoreModule,
    AppGridComponent,
    AppListComponent,
    PageComponent,
    PageHeaderComponent,
  ],
})
export class AppOverviewComponent implements OnInit, OnDestroy, Refreshable {
  readonly injector = inject(Injector);
  readonly;

  settings$ = this.store.observe(
    SOFTLINE_FEATURE_SETTINGS,
    SettingsStore.getters.values<ModuleSettings>(),
    SOFTLINE_SETTINGS_APP_OVERVIEW
  );

  private getTranslatedName(name: string): string {
    return this.store.get(
      SOFTLINE_FEATURE_TRANSLATION,
      TranslationStore.getters.translation,
      name
    );
  }

  search = '';

  applications$: Observable<readonly Application[]> = this.store.observe(
    SOFTLINE_FEATURE_APPLICATIONS,
    ApplicationStore.getters.items
  );

  readonly config = inject(SOFTLINE_CONFIG_MODULES, { optional: true });

  readonly linkModules = signal<LinkModule[]>([]);
  readonly appModules = toSignal(this.applications$, {
    initialValue: [] as Application[],
  });

  readonly applications = computed(() => {
    const apps = this.appModules().concat(this.linkModules());

    if (this.config && this.config?.preserveProviderOrder) {
      return apps;
    }

    const sorted = apps.sort((a, b) =>
      this.getTranslatedName(a.name).localeCompare(
        this.getTranslatedName(b.name),
        undefined,
        { sensitivity: 'base' }
      )
    );

    return sorted;
  });

  permissions$: Observable<readonly string[]> = this.store.observe(
    SOFTLINE_FEATURE_AUTHORIZATION,
    AuthorizationStore.getters.permissions
  );

  constructor(
    public store: Store,
    protected router: Router,
    private refreshService: RefreshService,
    @Optional()
    @Inject(SOFTLINE_CONFIG_MODULES_STATIC_LINKS)
    _staticLinks?: LinkModule[]
  ) {
    this.linkModules.set(_staticLinks ?? []);
  }

  async ngOnInit(): Promise<void> {
    this.refreshService.add(this);

    await this.store.dispatch(
      SOFTLINE_FEATURE_AUTHORIZATION,
      AuthorizationStore.actions.loadOnce
    );

    await this.store.dispatch(SOFTLINE_FEATURE_SETTINGS, SettingsStore.actions.load);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#APP_OVERVIEW.ALL_APPLICATIONS'
    );

    await this.setMode('grid');
  }

  ngOnDestroy(): void {
    this.refreshService.remove(this);
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
  }

  async refresh(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_AUTHORIZATION,
      AuthorizationStore.actions.load
    );
  }

  filterPermissions(
    application: Application,
    permissions: readonly string[] | undefined
  ): boolean {
    return (
      !application.permission || (permissions ?? []).indexOf(application.permission) > -1
    );
  }

  filterInput(
    application: Application,
    param?: { store: Store; input: string }
  ): boolean {
    if (!param) return false;
    const feature = SOFTLINE_FEATURE_TRANSLATION;
    const selector = TranslationStore.getters.translation;
    return param.store
      .get(feature, selector, application.name)
      .toLowerCase()
      .startsWith(param.input.toLowerCase());
  }

  async navigate(application: Application): Promise<void> {
    if (application?.type === 'link') {
      if (application?.['execute']) {
        await application.execute(this.injector);
      } else {
        window.open(application.href, '_blank');
      }
    } else {
      await this.router.navigate([application.route], application.routeParams);
    }
  }

  async setMode(mode: 'grid' | 'list'): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_SETTINGS,
      SettingsStore.mutations.setKey<ModuleSettings>(),
      { key: SOFTLINE_SETTINGS_APP_OVERVIEW, values: { display: mode } }
    );
    await this.store.dispatch(SOFTLINE_FEATURE_SETTINGS, SettingsStore.actions.save, {
      display: mode,
    });
  }
}
